//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { FormField } from '@campaigns/components/shared/product-details-modal/form/FormField';
import { ProductDetailsFieldLabels } from '@campaigns/constants/ProductDetailsModalFieldLabels';
import { getVariationsValidationRules } from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';

interface VariationBasicFieldsProps {
  isViewOnly: boolean;
}

export const VariationBasicFields = ({
  isViewOnly,
}: VariationBasicFieldsProps) => {
  const { control } = useFormContext<EditableBVSamplingProduct>();
  const variations = useWatch({ control, name: 'variations' });
  const hasVariations = variations.length > 1;

  const variationRules = getVariationsValidationRules(0, variations.length);

  if (hasVariations) {
    return null;
  }

  return (
    <Container>
      {/* Quantity Field */}
      <FormField
        inputType='text'
        isViewOnly={isViewOnly}
        label={ProductDetailsFieldLabels.quantity}
        labelHelpText="Enter the number of product units you're making available for this campaign. Count multipacks as single items."
        name='variations.0.quantity'
        placeholder='10'
        rules={variationRules['variations.0.quantity']}
        visibleToParticipants={false}
        width='half'
      />

      {/* FMV Field */}
      <FormField
        inputType='text'
        isViewOnly={isViewOnly}
        label={ProductDetailsFieldLabels.fairMarketValue}
        labelHelpText='Enter a fair market value that reflects the normal price of the product on your website or other retail sites.'
        name='variations.0.fairMarketValue'
        placeholder='25.99'
        rules={variationRules['variations.0.fairMarketValue']}
        visibleToParticipants={true}
        width='half'
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;
